import { useState } from "react";
import axios from "axios";
import { formatImportErrorMessage } from "../../utils/importErrorMessage";

const AREA_NAME_MAP = {
  tokyo: "东京",
  narita_airport: "成田机场",
  haneda_airport: "羽田机场",
  yokohama_port: "横滨港",
  hakone: "箱根",
  mt_fuji: "富士山",
  karuizawa: "轻井泽",
  nikko: "日光",
  kawagoe: "川越",
  shizuoka: "静冈",
  kanazawa: "金泽",
  okinawa: "冲绳",
  kamakura: "镰仓",
  odawara: "小田原",
  mishima: "三岛",
  takayama: "高山",
  shirakawago: "白川乡",
  nagoya: "名古屋",
  other: "其他",
};

export default function useAdminPricingConfig({ apiBaseUrl, setLoading }) {
  const [pricingTab, setPricingTab] = useState("areas");
  const [extraHoursConfig, setExtraHoursConfig] = useState([]);
  const [areas, setAreas] = useState([]);
  const [charterPricing, setCharterPricing] = useState([]);
  const [transferPricing, setTransferPricing] = useState([]);
  const [nightSurcharges, setNightSurcharges] = useState([]);
  const [overtimeRates, setOvertimeRates] = useState([]);
  const [, setEditingItem] = useState(null);
  const [, setShowAreaForm] = useState(false);
  const [, setShowSurchargeForm] = useState(false);
  const [, setShowBatchAreaForm] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState([]);

  const API = apiBaseUrl;

  const fetchAreas = async () => {
    try {
      const response = await axios.get(`${API}/config/areas`);
      setAreas(response.data || []);
    } catch (error) {
      console.error("获取区域配置失败:", error);
    }
  };

  const fetchPricingConfig = async () => {
    setLoading(true);
    try {
      const [areasRes, charterRes, transferRes, surchargeRes, overtimeRes, extraHoursRes] = await Promise.all([
        axios.get(`${API}/config/areas?enabled_only=false`),
        axios.get(`${API}/config/charter-pricing`),
        axios.get(`${API}/config/transfer-pricing`),
        axios.get(`${API}/config/night-surcharge`),
        axios.get(`${API}/config/overtime-rates`),
        axios.get(`${API}/config/extra-hours`),
      ]);
      setAreas(areasRes.data);
      setCharterPricing(charterRes.data);
      setTransferPricing(transferRes.data);
      setNightSurcharges(surchargeRes.data);
      setOvertimeRates(overtimeRes.data);
      setExtraHoursConfig(extraHoursRes.data);
    } catch (error) {
      console.error("获取定价配置失败", error);
    } finally {
      setLoading(false);
    }
  };

  const initializeAreas = async () => {
    try {
      await axios.post(`${API}/admin/config/init-areas`);
      fetchPricingConfig();
      alert("默认地区初始化成功！");
    } catch (error) {
      alert(formatImportErrorMessage(error, "初始化失败"));
    }
  };

  const importPresetData = async () => {
    if (!window.confirm("确定要导入预设数据吗？这将覆盖现有的地区和价格配置！")) {
      return;
    }

    try {
      setLoading(true);

      const response = await fetch("/preset_data.json");
      if (!response.ok) {
        throw new Error("无法加载预设数据文件");
      }
      const presetData = await response.json();

      const result = await axios.post(`${API}/admin/config/bulk-import`, presetData);

      alert(`数据导入成功！\n- 地区: ${result.data.imported.areas || 0} 条\n- 包车价格: ${result.data.imported.charter_pricing || 0} 条\n- 单程价格: ${result.data.imported.transfer_pricing || 0} 条\n- 超时费率: ${result.data.imported.overtime_rates || 0} 条`);

      fetchPricingConfig();
    } catch (error) {
      alert(formatImportErrorMessage(error, "导入失败"));
    } finally {
      setLoading(false);
    }
  };

  const toggleAreaSelection = (areaCode) => {
    setSelectedAreas((prev) =>
      prev.includes(areaCode)
        ? prev.filter((code) => code !== areaCode)
        : [...prev, areaCode]
    );
  };

  const toggleAllAreas = () => {
    if (selectedAreas.length === areas.length) {
      setSelectedAreas([]);
    } else {
      setSelectedAreas(areas.map((area) => area.code));
    }
  };

  return {
    areaNameMap: AREA_NAME_MAP,
    areas,
    charterPricing,
    extraHoursConfig,
    fetchAreas,
    fetchPricingConfig,
    importPresetData,
    initializeAreas,
    nightSurcharges,
    overtimeRates,
    pricingTab,
    selectedAreas,
    setEditingItem,
    setExtraHoursConfig,
    setPricingTab,
    setShowAreaForm,
    setShowBatchAreaForm,
    setShowSurchargeForm,
    toggleAllAreas,
    toggleAreaSelection,
    transferPricing,
  };
}
